<template>
  <div>
    <vs-alert v-if="limitExhausted" title="Your Have Exhausted Your Limit" color="warning">
      You have exhausted your automation limit for current billing cycle. Your task limit was <b>{{ limitExhaustedData.taskLimit }}</b> and your current task
      count is <b>{{ limitExhaustedData.usage }}</b
      >. You can either wait for the next billing cycle to start that will reset your task limit or you can upgrade your plan to increase your task limit.
    </vs-alert>
    <vs-alert v-if="noPlanExits" title="You Do Not Have Any Active Subscription" color="warning">
      It seems that you don't currently have an active subscription with us. However, rest assured that your automations are still running smoothly. We have
      simply restricted your access to monitor them. If you would like to regain access, you can purchase a subscription at any time.
    </vs-alert>
    <vx-card v-if="currentPlan !== null" class="mb-6">
      <h4 class="mb-4">Your Current Subscription Plan</h4>
      <div class="flex flex-row justify-between">
        <div class="flex flex-row">
          <img :src="getCurrentSubsImage(currentPlan.currentProductId, stripePlans)" />
          <div class="ml-6">
            <div class="flex flex-row">
              <h5 class="mt-2">{{ currentPlan.currentPlanName }}</h5>
              <p :class="currentPlan.status === 'active' ? 'active-badge p-1 text-center ml-4 mt-2' : 'trailing-badge p-1 text-center ml-4 mt-2'">
                {{ currentPlan.status[0].toUpperCase() + currentPlan.status.substring(1) }}
              </p>
            </div>
            <div class="flex flex-row mt-2">
              <p>Billing {{ currentPlan.billingCycle[0].toUpperCase() + currentPlan.billingCycle.substring(1) }}ly</p>
              <p class="ml-5">
                Current Billing Cycle ends on <span class="font-bold">{{ currentPlan.endTime }}</span>
              </p>
              <p v-if="whenCancel" class="ml-5">
                Scheduled Cancellation On <span class="font-bold">{{ whenCancel }}</span>
              </p>
            </div>
          </div>
        </div>

        <vs-button @click="confirmCancel(currentPlan.currentSubscriptionId)" :color="willCancel ? 'warning' : 'danger'" type="relief" class="cancel-btn">{{
          willCancel ? 'Resume Subscription' : 'Cancel Subscription'
        }}</vs-button>
      </div>
    </vx-card>
    <div class="text-center">
      <vs-alert
        v-if="
          subscriptionScheduleStatus === 'active' &&
          currentPlan.currentPlanPriceId !== subscriptionSchedulePriceId &&
          currentPlan.currentPlanName !== subscriptionSchedulePlanName
        "
        title="Downgrade Membership"
        color="warning"
      >
        Your Lighthouse subscription will be downgraded to {{ subscriptionSchedulePlanName }} at the end of this billing cycle ({{ currentPlan.endTime }}) as
        per your request.
        <br />
        <vs-button @click="confirmCancelDowngrade()" :color="'warning'" type="relief" class="cancel-downgrade"> Cancel Scheduled Downgrade</vs-button>
      </vs-alert>
    </div>
    <div class="text-center mt-10">
      <h2 class="text-gray-700">Choose a plan</h2>
      <p class="mt-3 text-gray-600">Select a plan that suits your automation needs. We serve a variety of plans with different features and pricing.</p>
    </div>
    <!-- this is the toggle section which is used to switch the monthly and yearly prices -->
    <div class="flex flex-row justify-center mt-5">
      <span class="mx-2 font-medium">Monthly</span><vs-switch color="rgb(152, 14, 243)" v-model="switchPrice" /><span class="mx-2 font-medium">Yearly</span>
    </div>
    <!-- This is the Pricing cards Section -->
    <div class="flex flex-row flex-1 gap-4 mt-12 pricing-cards">
      <vx-card
        v-for="(plan, index) in stripePlans"
        :key="index"
        :class="
          checkIfActivePrice(switchPrice ? plan.billingCycle.yearly.multiPriceIds : plan.billingCycle.monthly.multiPriceIds)
            ? 'planCard border-success'
            : 'planCard border-primary'
        "
      >
        <div class="flex flex-row plansName">
          <img :src="plan.icon.url" />
          <div class="ml-4">
            <p class="font-medium">{{ plan.planName }}</p>
            <p
              :class="
                checkIfActivePrice(switchPrice ? plan.billingCycle.yearly.multiPriceIds : plan.billingCycle.monthly.multiPriceIds)
                  ? 'text-success'
                  : 'text-primary'
              "
            >
              <span class="text-2xl font-semibold">
                {{ currencySymbol }}{{ switchPrice ? plan.billingCycle.yearly.amount / 12 : plan.billingCycle.monthly.amount }}</span
              ><span class="text-base">/mo</span>
            </p>
            <p v-if="switchPrice" class="active-badge-1 p-1">Save {{ getDiscountAmount(plan.billingCycle.monthly.amount, plan.billingCycle.yearly.amount) }}</p>
          </div>
        </div>
        <vs-divider />
        <p class="mt-3 font-semibold">{{ plan.planMetaData.suitableFor }}</p>
        <div class="mt-4 planDetails">
          <ul class="list-disc">
            <li class="mt-2">&#10004; {{ plan.planMetaData.numberOfApplications }} {{ tag === 'Lighthouse' ? 'Workflows' : 'Applications' }}</li>
            <li class="mt-2">&#10004; {{ plan.planMetaData.numberOfTransactions }} transactions</li>
            <li class="mt-2">&#10004; {{ plan.planMetaData.emailResponseWindowHours }} hrs email response</li>
            <li class="mt-2">
              &#10004; {{ plan.planMetaData.historicalDataPeriodDays === -1 ? 'Unlimited' : plan.planMetaData.historicalDataPeriodDays }} days Historical Data
            </li>
          </ul>
        </div>

        <vs-button
          @click="subscribeToPlan(switchPrice ? plan.billingCycle.yearly.multiPriceIds : plan.billingCycle.monthly.multiPriceIds)"
          :color="checkIfActivePrice(switchPrice ? plan.billingCycle.yearly.multiPriceIds : plan.billingCycle.monthly.multiPriceIds) ? 'success' : 'primary'"
          class="w-full mt-5 text-sm"
        >
          {{
            checkIfActivePrice(switchPrice ? plan.billingCycle.yearly.multiPriceIds : plan.billingCycle.monthly.multiPriceIds)
              ? 'Active Plan'
              : 'Choose ' + plan.planName
          }}
        </vs-button>
      </vx-card>
    </div>
    <proration :previewInvoice="previewInvoice" ref="prorationPreviewPopup"></proration>
  </div>
</template>

<script>
import proration from './Proration.vue'
import { epochToDate, getStripeForCloudifyUrl, getCurrencySymbol } from '@/utils/misc'
import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/functions'
import axios from 'axios'

export default {
  name: 'PricingPage',
  components: { proration },
  props: ['noPlanExits', 'limitExhausted', 'limitExhaustedData'],
  data() {
    return {
      switchPrice: false,
      currentPlan: null,
      stripeCustomerId: null,
      prorationPreview: null,
      willCancel: false,
      whenCancel: null,
      cards: [],
      currency: null,
      tag: null,
      firestoreDoc: null,
      previewInvoice: null,
      subscriptionScheduleStatus: null,
      subscriptionSchedulePlanName: null,
      subscriptionScheduledId: null,
      subscriptionSchedulePriceId: null,
      paymentMethodPresent: false,
      addPaymentPrompt: false,
      currencySymbol: null,
      currentPriceIds: null,
    }
  },
  computed: {
    stripePlans() {
      return this.$store.getters['stripe/getStripePlans']
    },
  },
  methods: {
    /**
     * This function is shows a prompt to the user to confirm the cancellation of the subscription
     */
    confirmCancel() {
      const dialogTitle = this.willCancel ? 'Resume Subscription?' : 'Cancel Subscription?'
      const dialogText = this.willCancel
        ? 'Do you want to revoke your request for cancellation?'
        : 'We noticed that you have an active subscription with us. If you choose to cancel your subscription, it will be terminated at the end of this billing cycle, including any scheduled subscriptions. Are you sure you want to cancel?'
      this.$vs.dialog({
        type: 'confirm',
        color: this.willCancel ? 'warning' : 'danger',
        title: dialogTitle,
        text: dialogText,
        accept: this.cancelSubscription,
      })
    },
    /**
     * This function is shows a prompt to the user to confirm the cancellation of the scheduled downgrade
     */
    confirmCancelDowngrade() {
      const dialogTitle = 'Cancel Scheduled Downgrade'
      const dialogText = `It appears that you have requested to cancel ${this.subscriptionSchedulePlanName} which is supposed to start at ${this.currentPlan.endTime}. Please click on "Accept" to confirm your cancellation.`
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: dialogTitle,
        text: dialogText,
        accept: this.cancelDowngrade,
      })
    },
    /**
     * This method will cancel all the active scheduled subscriptions
     */
    async cancelDowngrade() {
      this.$vs.notify({
        title: `Cancelling the Scheduled Subscription`,
        text: 'Please Wait . . .',
        color: 'primary',
        time: 6000,
        position: 'top-right',
      })
      try {
        if (this.subscriptionScheduleStatus === 'active') {
          const { data: deleteSchedule } = await axios.post(`${getStripeForCloudifyUrl()}/cancelSchedule`, {
            stripeCustomerID: this.stripeCustomerId,
          })
          this.$rollbar.info('Subscription which was scheduled was cancelled', { data: deleteSchedule })
        }
        setTimeout(() => {
          this.$router.go()
        }, 5000)
      } catch (error) {
        this.$rollbar.error(`Error Occurred While Canceling Scheduled Subscription: ${this.subscriptionScheduledId}`, { error })
      }
    },

    /**
     * This method is used to cancel a active or trailing subscription
     */
    async cancelSubscription() {
      try {
        if (this.subscriptionScheduleStatus === 'active') {
          const { data: deleteSchedule } = await axios.post(`${getStripeForCloudifyUrl()}/cancelSchedule`, {
            stripeCustomerID: this.stripeCustomerId,
          })
          this.$rollbar.info('Subscription which was scheduled was cancelled', { data: deleteSchedule })
        }
        const { data: cancelRes } = await axios.post(`${getStripeForCloudifyUrl()}/stripeCancelSubscription`, {
          subscriptionId: this.currentPlan.currentSubscriptionId,
          // eslint-disable-next-line no-unneeded-ternary
          willCancel: this.willCancel ? true : false,
        })
        this.$rollbar.info('Subscription Was Canceled Successfully', { data: cancelRes })
        this.$router.go()
      } catch (error) {
        this.$rollbar.error(`Error Occurred While Canceling Subscription: ${this.currentPlan.currentSubscriptionId}`, { error })
      }
    },

    /**
     * This function checks if the current iteration price is the active price
     * @param {String}  priceId The price id of the plan
     */
    checkIfActivePrice(priceId) {
      if (this.currentPlan === null) return false
      else if (this.currentPlan.currentPlanPriceId === priceId[0].value) {
        this.currentPriceIds = priceId
        return true
      } else return false
    },

    /**This function is responsible for getting the amount that they will save if they switch to annual plan*/
    getDiscountAmount(monthlyPrice, yearlyPrice) {
      const x = monthlyPrice * 12
      const y = parseInt(((x - yearlyPrice) / x) * 100)
      if (!y) {
        return '0% off'
      } else {
        return `${y}% off`
      }
    },
    /**
     * This function is responsible for deciding wether we want to prorate to a different plan or is it a new subscription all together.
     * @param {String} priceId This the price id of the target card that the user clicked on.
     */
    async subscribeToPlan(priceId) {
      if (!this.willCancel) {
        this.$vs.loading()
        if (this.currentPlan === null) {
          try {
            //this means that we are subscribing to a new plan as we don't have an existing subscription.
            const { data: checkoutObject } = await axios.post(`${getStripeForCloudifyUrl()}/stripeStartSubscription`, {
              customer: this.stripeCustomerId,
              revertBackUrl: window.location.href,
              priceId,
              ownerId: JSON.parse(localStorage.getItem('userInfo')).uid,
              country: this.firestoreDoc.user.address.country,
              yearly: this.switchPrice,
            })
            this.$rollbar.info(`New Subscription started for ${this.stripeCustomerId}`)
            window.open(checkoutObject.url, '_self')
          } catch (error) {
            this.$rollbar.error(`Error While Starting A New Subscription`, { error })
          }
        } else if (!this.checkIfActivePrice(priceId)) {
          if (this.paymentMethodPresent) {
            //this means that we are prorating to a different plan, but first we have to check weather the selected priceID is already active or not.
            await this.stripeGetProrationPreview(priceId)
          } else {
            this.$vs.loading.close()

            this.$vs.dialog({
              type: 'confirm',
              color: 'primary',
              title: 'Payment Method Not added',
              text: `Please add payment method to continue`,
              accept: this.addPaymentMethod,
              acceptText: 'Add Payment',
            })
          }
        }
      } else {
        this.$vs.dialog({
          type: 'alert',
          color: 'danger',
          title: "Can't Upgrade or Downgrade Subscription",
          text: `You have already scheduled cancelletion of the current subscription. Please revoke the cancellation (resume the subscription) and then select other plan if you want to Upgrade or Downgrade`,
        })
      }
      this.$vs.loading.close()
    },
    /**
     * This function is used to create a new card from autocode stripe endpoint and it will return a object that contains a url to create a new payment card.
     * @param {string} stripeCustomerId This is the stripeCustomerId of the user.
     * @param {string} revertBackUrl This is the revertBackUrl we used to navigate the user after the successfull payment card added
     */
    async addPaymentMethod() {
      try {
        const { data: res } = await axios.post(`${getStripeForCloudifyUrl()}/stripeAddCardPayment`, {
          customer: this.stripeCustomerId,
          revertBackUrl: window.location.href,
        })
        this.$rollbar.info(`Added Payment Method for ${this.stripeCustomerId}`, { data: res })
        window.open(res.url, '_self')
      } catch (error) {
        this.$rollbar.error(`Error Occurred While Adding A New Card For Customer: ${this.stripeCustomerId}`, { error })
      }
    },

    /**
     * This function is responsible for getting the proration preview invoice from stripe that will infrom the user about the charges that will be reflected in next billing cycle post upgrade/downgrade
     * @param {String} priceId This the price id of the target card that the user clicked on.
     */
    async stripeGetProrationPreview(priceId) {
      try {
        const prorationPreviewResponse = await axios.post(`${getStripeForCloudifyUrl()}/stripePreviewProration`, {
          customerId: this.stripeCustomerId,
          subscriptionId: this.currentPlan.currentSubscriptionId,
          newPriceId: priceId[0].value,
        })
        if (prorationPreviewResponse.data.intervalMismatch) {
          this.$vs.dialog({
            color: 'danger',
            title: `Important Note`,
            text: `Please note that you cannot change your subscription from Yearly to Monthly, or from Monthly to Yearly. However, you can still make changes to your
        subscription by upgrading or downgrading to a different plan with the same billing interval as your current plan. This means that you have the
        flexibility to choose a plan that best fits your needs while staying within the same billing cycle.`,
          })
        } else {
          this.$refs.prorationPreviewPopup.popupActivo = true
          this.previewInvoice = {
            isUpgrade: prorationPreviewResponse.data.currentBillingCycle.amount < prorationPreviewResponse.data.updateBillingCycle.amount,
            currency: this.currency,
            currentPlanName: prorationPreviewResponse.data.currentBillingCycle.planName,
            currentPlanPrice: prorationPreviewResponse.data.currentBillingCycle.amount,
            currentPlanEndDate: prorationPreviewResponse.data.currentBillingCycle.end,
            currentPlanStatus: this.currentPlan.status,
            currentPlanInterval: prorationPreviewResponse.data.currentBillingCycle.interval,
            updatePlanName: prorationPreviewResponse.data.updateBillingCycle.planName,
            updatePlanPrice: prorationPreviewResponse.data.updateBillingCycle.amount,
            updatePlanInterval: prorationPreviewResponse.data.updateBillingCycle.interval,
            unUsedTimeAmount: prorationPreviewResponse.data.updateBillingCycle.unUsedTimeAmount,
            subscriptionId: this.currentPlan.currentSubscriptionId,
            newPriceId: priceId,
            currentPlanPriceId: this.currentPriceIds,
            country: this.firestoreDoc.user.address.country,
            customer: this.stripeCustomerId,
          }
        }
      } catch (error) {
        this.$rollbar.error(`Error While Preview Stripe Proration for ${this.stripeCustomerId}`, { error })
      }
    },

    /**
     * @description This function is responsible to show the notification if the subcription schedule is active
     */
    async getActiveScheduleSubscription() {
      if (this.currentPlan !== null) {
        try {
          const activeSchedule = await axios.post(`${getStripeForCloudifyUrl()}/stripeGetSubscriptionScheduleStatus`, {
            customerId: this.stripeCustomerId,
            subscriptionId: this.currentPlan.currentSubscriptionId,
            currentPlanStartTime: this.currentPlan.startTime,
          })

          this.subscriptionScheduleStatus = activeSchedule.data.status
          this.subscriptionSchedulePlanName = activeSchedule.data.planName
          this.subscriptionScheduledId = activeSchedule.data.scheduledId
          this.subscriptionSchedulePriceId = activeSchedule.data.priceId
        } catch (error) {
          this.$rollbar.error(`Error While Fetching the Active Schedule Subscription`, { error })
        }
      }
    },

    /**
     * @description This function fetches stripe subscription from customer/subscription collection with status as active or trialing.
     */
    async findCurrentSubscription() {
      try {
        //get subscription details
        const subs = this.$store.state.subscriptions

        if (subs !== 'undefined') {
          const currentPlanObject = subs
          this.currentPlan = {
            startTime: epochToDate(currentPlanObject.current_period_start.seconds, true),
            endTime: epochToDate(currentPlanObject.current_period_end.seconds, true),
            status: currentPlanObject.status,
            billingCycle: currentPlanObject.items[0].plan.interval,
            currentPlanName: currentPlanObject.items[0].plan.nickname,
            currentPlanPriceId: currentPlanObject.items[0].plan.id,
            currentSubscriptionId: currentPlanObject.items[0].subscription,
            currentProductId: currentPlanObject.items[0].plan.product,
            cancelAtEnd: currentPlanObject.cancel_at_period_end,
          }
          this.willCancel = currentPlanObject.cancel_at
          if (this.willCancel) this.whenCancel = epochToDate(this.willCancel.seconds, true)
          this.switchPrice = this.currentPlan.billingCycle !== 'month'
        } else {
          console.log('no subscription found')
        }

        //get customer details
        const userId = this.$store.state.admin.access ? this.$store.state.DB.owner : firebase.auth().currentUser.uid
        const cust = await firebase.firestore().collection('customers').doc(userId).get()
        if (!cust.exists) {
          console.log('No stripe customer found in firestore')
        } else {
          this.stripeCustomerId = await cust.data().stripeId
        }
      } catch (error) {
        this.$rollbar.error(`Error While fetching current Subscription`, { error })
      }
    },
    /**
     * This function is responsible for getting the current Subscription image.
     * @param {string} currentProductId this is the current product id which user subscribed.
     * @param {array} stripePlans this stripePlans consists all plans related to both Lighthouse and Marketplace.
     * @returns {string} Returns the current subscription icon.
     */
    getCurrentSubsImage(currentProductId, stripePlans) {
      for (const plan of stripePlans) {
        if (plan.planMetaData.productId === currentProductId) {
          return plan.icon.url
        }
      }
    },
    async fetchFirestoreDoc() {
      try {
        const firestoreDocData = this.$store.state.DB

        this.currency = firestoreDocData.additionalPlanInformation.currencySelected
        this.currencySymbol = getCurrencySymbol(this.currency)
        this.tag = firestoreDocData.user.tag.type
        this.firestoreDoc = firestoreDocData
      } catch (error) {
        this.$rollbar.error(`Error While Fetching Firestore Doc In Pricing Page`, { error })
      }
    },
    async getPaymentDetails() {
      const { data: res } = await axios.get(`${getStripeForCloudifyUrl()}/getCustomerPaymentMethods?customerId=${this.stripeCustomerId}`)
      if (res.data.length > 0) {
        this.paymentMethodPresent = true
      }
    },
  },

  async created() {
    this.$vs.loading()
    setTimeout(async () => {
      await this.fetchFirestoreDoc()
      this.$store.dispatch('stripe/extractStripeData', { currencySelected: this.currency, tagType: this.tag })
      await this.findCurrentSubscription()
      await this.getActiveScheduleSubscription()
      await this.getPaymentDetails()
      this.$vs.loading.close()
    }, 4000)
  },
}
</script>

<style scoped>
.planCard {
  border-top: 5px solid;
  border-radius: 0.5em;
  animation: planCardFadeIn ease 2s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
}

.cancel-downgrade {
  margin-top: 10px;
}

.planDetails {
  height: 170px;
}
.plansName {
  height: 100px;
}
.price {
  color: rgb(152, 14, 243);
}
@keyframes planCardFadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.textx {
  color: rgb(152, 14, 243);
}

.choose-button-plan {
  background-color: rgb(152, 14, 243);
  all: unset;
  cursor: pointer;
  color: white;
  padding-top: 0.8em;
  padding-bottom: 0.8em;
  padding-left: 1.5em;
  padding-right: 1.5em;
  border-radius: 0.5em;
  transition: all 0.3s ease 0s;
  font-size: 12px;
  width: 85%;
  text-align: center;
}
.active-btn {
  background-color: #28c76f;
  width: 100%;
  font-size: 12px;
}
.choose-button-plan:hover {
  box-shadow: 0px 5px 5px rgba(137, 12, 221, 0.801);
  color: #fff;
  transform: translateY(-7px);
}
.active-btn:hover {
  box-shadow: 0px 5px 5px rgba(0, 255, 132, 0.8);
  color: #fff;
  transform: translateY(-7px);
}
.currentPlanDetailsTitle {
  border-bottom: 2px solid rgb(152, 14, 243);
  border-radius: 2px;
}
.cancel-btn {
  height: 51px;
  width: 199px;
}
.active-badge {
  height: 23px;
  background-color: #dff6de;
  border-radius: 3px;
  color: #70cd7a;
  font-weight: 600;
  font-size: 13px;
}
.trailing-badge {
  background-color: #ff8f0e;
  border-radius: 3px;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
}
.active-badge-1 {
  height: 25px;
  background: #dff6de;
  border-radius: 3px;
  font-size: 12px;
  color: #70cd7a;
  font-weight: 500;
  width: 100%;
}
img {
  height: 69px;
  width: 69px;
}
@media screen and (max-width: 992px) {
  .pricing-cards {
    flex-wrap: wrap;
  }
}
.upgrade-plan {
  background-color: #dff6de;
  border-radius: 5px;
}
.upgrade-downgrade-btn {
  height: 51px;
  color: #ffff;
  font-size: 15px;
  font-weight: 600;
}
.disscount {
  background-color: #f5db34;
  color: #000000;
  font-size: 12px;
  font-weight: 500;
  border-radius: 3px;
}
</style>
